import { AuthenticationError, PermissionDeniedError } from "@whop/sdk";
import chalk from "chalk";
import { z } from "incur";
import { resolveActiveAccountId } from "../api/index.js";
import {
	apiBaseUrl,
	validateApiKey,
	type ValidateResult,
} from "../auth/client.js";
import { listOAuthAccounts } from "../auth/oauth.js";
import {
	getActiveProfile,
	getValidAccessToken,
	setActiveProfileAccount,
} from "../auth/profiles.js";
import {
	type AccountChoice,
	type AccountOrCreateChoice,
	PromptCancelledError,
	promptBusinessName,
	selectAccountOrCreate,
	startSpinner,
} from "../auth/prompts.js";
import type { Profile } from "../auth/types.js";
import { c as colors } from "../lib/colors.js";
import { makeWhopRequest } from "./client.js";

const accountOutput = z.object({
	id: z.string().describe("The selected business account ID"),
	title: z.string().nullable().describe("The business account name"),
	route: z.string().nullable().describe("The business account route"),
});

export const quickstartOptions = z.object({
	business_name: z
		.string()
		.optional()
		.describe("Name for a new business account (OAuth logins only)"),
	account_id: z
		.string()
		.optional()
		.describe("Existing business account to use (biz_xxx)"),
});

export const quickstartOutput = z.object({
	account: accountOutput.describe(
		"The business account future CLI commands will use",
	),
	created: z
		.boolean()
		.describe("Whether this run created the selected business account"),
});

type Account = z.infer<typeof accountOutput>;
type CtaCommand = {
	command: string;
	description?: string;
	options?: Record<string, string>;
};

type RunCtx = {
	agent: boolean;
	formatExplicit: boolean;
	options: z.infer<typeof quickstartOptions>;
	ok: (data: z.infer<typeof quickstartOutput>) => never;
	error: (opts: {
		code: string;
		message: string;
		retryable?: boolean;
		exitCode?: number;
		cta?: { description?: string; commands: CtaCommand[] };
	}) => never;
};

type Spinner = { stop: (message: string) => void };

export type QuickstartDependencies = {
	getActiveProfile: () => Profile | null;
	getValidAccessToken: (profile: Profile) => Promise<string | null>;
	listOAuthAccounts: (
		accessToken: string,
		options?: { stopAtId?: string },
	) => Promise<AccountChoice[]>;
	resolveActiveAccountId: () => Promise<string | null | undefined>;
	validateApiKey: (apiKey: string) => Promise<ValidateResult>;
	createAccount: (name: string, accessToken: string) => Promise<Account>;
	setActiveProfileAccount: (account: Account, profileName: string) => boolean;
	selectAccountOrCreate: (
		accounts: AccountChoice[],
		currentId?: string,
	) => Promise<AccountOrCreateChoice>;
	promptBusinessName: () => Promise<string>;
	startSpinner: (message: string) => Spinner;
};

const defaultDependencies: QuickstartDependencies = {
	getActiveProfile,
	getValidAccessToken,
	listOAuthAccounts,
	resolveActiveAccountId,
	validateApiKey: (apiKey) => validateApiKey(apiKey, apiBaseUrl()),
	createAccount: async (name, accessToken) => {
		const account = await makeWhopRequest<{
			id: string;
			title?: string | null;
			route?: string | null;
		}>("POST", "/accounts", { title: name }, accessToken);
		return {
			id: account.id,
			title: account.title ?? null,
			route: account.route ?? null,
		};
	},
	setActiveProfileAccount: (account, profileName) =>
		setActiveProfileAccount(
			{
				id: account.id,
				title: account.title ?? undefined,
				route: account.route ?? undefined,
			},
			profileName,
		),
	selectAccountOrCreate,
	promptBusinessName,
	startSpinner,
};

export async function runQuickstart(
	ctx: RunCtx,
	dependencies: QuickstartDependencies = defaultDependencies,
): Promise<never> {
	const profile = dependencies.getActiveProfile();
	if (!profile && !process.env.WHOP_API_KEY) {
		return ctx.error({
			code: "NOT_AUTHENTICATED",
			message: "Not logged in.",
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	if (ctx.options.account_id && ctx.options.business_name !== undefined) {
		return ctx.error({
			code: "CONFLICTING_OPTIONS",
			message:
				"Choose an existing business with --account_id or create one with --business_name, not both.",
			retryable: true,
		});
	}

	const businessName = ctx.options.business_name?.trim();
	if (ctx.options.business_name !== undefined && !businessName) {
		return ctx.error({
			code: "INVALID_BUSINESS_NAME",
			message: "--business_name cannot be blank.",
			retryable: true,
		});
	}

	try {
		if (profile?.method === "oauth") {
			return await runOAuthQuickstart(ctx, dependencies, profile, businessName);
		}
		return await runApiKeyQuickstart(ctx, dependencies, profile, businessName);
	} catch (error) {
		if (error instanceof PromptCancelledError) {
			return ctx.error({
				code: "CANCELLED",
				message:
					"Business setup cancelled. You're still logged in and can run `whop quickstart` to continue.",
				exitCode: 130,
				cta: {
					commands: [
						{
							command: "quickstart",
							description: "Continue business setup",
						},
					],
				},
			});
		}
		throw error;
	}
}

async function runOAuthQuickstart(
	ctx: RunCtx,
	dependencies: QuickstartDependencies,
	profile: Profile,
	businessName: string | undefined,
): Promise<never> {
	let accessToken: string | null;
	try {
		accessToken = await dependencies.getValidAccessToken(profile);
	} catch {
		accessToken = null;
	}
	if (!accessToken) {
		return ctx.error({
			code: "SESSION_UNAVAILABLE",
			message:
				"Couldn't refresh your session. Try again, or log in again if the problem continues.",
			retryable: true,
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	if (businessName) {
		return createAndSelectAccount(
			ctx,
			dependencies,
			profile,
			businessName,
			accessToken,
		);
	}

	const interactive = !ctx.agent && !ctx.formatExplicit;
	const spinner = interactive
		? dependencies.startSpinner("Loading your business accounts…")
		: null;
	let accounts: AccountChoice[];
	try {
		accounts = await dependencies.listOAuthAccounts(accessToken, {
			stopAtId: ctx.options.account_id,
		});
		spinner?.stop(
			`Found ${accounts.length} business account${accounts.length === 1 ? "" : "s"}.`,
		);
	} catch (error) {
		spinner?.stop("Couldn't load your business accounts.");
		if (
			error instanceof AuthenticationError ||
			error instanceof PermissionDeniedError
		) {
			return ctx.error({
				code: "ACCOUNT_ACCESS_DENIED",
				message:
					"Your session can't access your business accounts. Log in again to refresh access.",
				retryable: false,
				cta: {
					commands: [{ command: "auth login", description: "Log in to Whop" }],
				},
			});
		}
		return ctx.error({
			code: "ACCOUNT_LOOKUP_FAILED",
			message: "Couldn't load your business accounts. Please try again.",
			retryable: true,
		});
	}

	if (ctx.options.account_id) {
		const account = accounts.find(
			(candidate) => candidate.id === ctx.options.account_id,
		);
		if (!account) {
			return ctx.error({
				code: "ACCOUNT_NOT_FOUND",
				message: availableAccountsMessage(
					`No business account "${ctx.options.account_id}" for this user.`,
					accounts,
				),
				retryable: true,
				cta: accountSelectionCta(accounts, true),
			});
		}
		return selectAccount(ctx, dependencies, profile, account);
	}

	if (!interactive) {
		if (accounts.length === 0) {
			return ctx.error({
				code: "NO_ACCOUNTS",
				message:
					"You don't have a business account yet. Pass --business_name to create one.",
				retryable: true,
				cta: createAccountCta(),
			});
		}
		if (accounts.length > 1) {
			return ctx.error({
				code: "ACCOUNT_REQUIRED",
				message: availableAccountsMessage(
					"Choose which business account to use with --account_id.",
					accounts,
				),
				retryable: true,
				cta: accountSelectionCta(accounts, true),
			});
		}
		return selectAccount(ctx, dependencies, profile, accounts[0]);
	}

	if (accounts.length === 0) {
		console.log(
			`\n${chalk.bold("You don't have a business account yet — let's create one.")}`,
		);
		return createAndSelectAccount(
			ctx,
			dependencies,
			profile,
			await dependencies.promptBusinessName(),
			accessToken,
		);
	}

	const selection = await dependencies.selectAccountOrCreate(
		accounts,
		profile.accountId,
	);
	if (selection.kind === "create") {
		return createAndSelectAccount(
			ctx,
			dependencies,
			profile,
			await dependencies.promptBusinessName(),
			accessToken,
		);
	}
	return selectAccount(ctx, dependencies, profile, selection.account);
}

async function runApiKeyQuickstart(
	ctx: RunCtx,
	dependencies: QuickstartDependencies,
	profile: Profile | null,
	businessName: string | undefined,
): Promise<never> {
	if (businessName) {
		return ctx.error({
			code: "BUSINESS_CREATION_UNAVAILABLE",
			message:
				"API keys are tied to one business account. Log in with Whop to create or choose another business.",
			retryable: false,
			cta: {
				commands: [
					{
						command: "auth login",
						description: 'Choose "Log in with Whop"',
					},
				],
			},
		});
	}

	let validatedIdentity: {
		id?: string;
		title?: string;
		route?: string;
	} | null = null;
	if (profile?.method === "api_key") {
		const apiKey = await dependencies.getValidAccessToken(profile);
		if (!apiKey) return apiKeyAccountError(ctx);
		const validation = await dependencies.validateApiKey(apiKey);
		if (validation.status === "invalid") {
			return ctx.error({
				code: "INVALID_API_KEY",
				message:
					"The active API key was rejected. Log in again with a valid key.",
				retryable: false,
				cta: {
					commands: [{ command: "auth login", description: "Log in to Whop" }],
				},
			});
		}
		if (validation.status === "error") {
			return ctx.error({
				code: "ACCOUNT_LOOKUP_FAILED",
				message: `Couldn't validate the active API key: ${validation.message}`,
				retryable: true,
			});
		}
		validatedIdentity = validation.identity;
	}

	const accountId =
		validatedIdentity?.id ?? (await dependencies.resolveActiveAccountId());
	if (!accountId) return apiKeyAccountError(ctx);
	if (ctx.options.account_id && ctx.options.account_id !== accountId) {
		return ctx.error({
			code: "ACCOUNT_NOT_AVAILABLE",
			message: `This API key is tied to ${accountId}, not ${ctx.options.account_id}.`,
			retryable: false,
		});
	}

	const account = {
		id: accountId,
		title:
			validatedIdentity?.title ??
			(profile?.accountId === accountId
				? (profile.accountTitle ?? null)
				: null),
		route:
			validatedIdentity?.route ??
			(profile?.accountId === accountId
				? (profile.accountRoute ?? null)
				: null),
	};
	printSummary(ctx, account, false);
	return ctx.ok({ account, created: false });
}

function apiKeyAccountError(ctx: RunCtx): never {
	return ctx.error({
		code: "NO_ACCOUNT",
		message:
			"Couldn't resolve the business account for this API key. Check the key, or log in again.",
		retryable: true,
		cta: {
			commands: [{ command: "auth login", description: "Log in to Whop" }],
		},
	});
}

async function createAndSelectAccount(
	ctx: RunCtx,
	dependencies: QuickstartDependencies,
	profile: Profile,
	name: string,
	accessToken: string,
): Promise<never> {
	let account: Account;
	try {
		account = await dependencies.createAccount(name.trim(), accessToken);
	} catch (error) {
		return ctx.error({
			code: "ACCOUNT_FAILED",
			message: `Failed to create the business account: ${error instanceof Error ? error.message : "unknown error"}`,
			retryable: true,
		});
	}

	const persistenceFailure = persistAccount(
		dependencies,
		account,
		profile.name,
	);
	if (persistenceFailure?.kind === "inactive") {
		return ctx.error({
			code: "ACTIVE_PROFILE_CHANGED",
			message: `Business account "${account.title ?? name}" (${account.id}) was created and selected for profile "${profile.name}", but another profile became active.`,
			retryable: false,
			cta: {
				commands: [
					{
						command: `auth switch ${profile.name}`,
						description: `Switch back to profile "${profile.name}"`,
					},
				],
			},
		});
	}
	if (persistenceFailure) {
		return ctx.error({
			code: "ACCOUNT_CREATED_PROFILE_UPDATE_FAILED",
			message: `Business account "${account.title ?? name}" (${account.id}) was created, but profile "${profile.name}" could not be updated${persistenceFailure.detail ? `: ${persistenceFailure.detail}.` : "."} Log in again to select it.`,
			retryable: false,
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	printSummary(ctx, account, true);
	return ctx.ok({ account, created: true });
}

function selectAccount(
	ctx: RunCtx,
	dependencies: QuickstartDependencies,
	profile: Profile,
	account: AccountChoice,
): never {
	const persistenceFailure = persistAccount(
		dependencies,
		account,
		profile.name,
	);
	if (persistenceFailure?.kind === "inactive") {
		return ctx.error({
			code: "ACTIVE_PROFILE_CHANGED",
			message: `Business account "${account.title}" (${account.id}) was selected for profile "${profile.name}", but another profile became active.`,
			retryable: false,
			cta: {
				commands: [
					{
						command: `auth switch ${profile.name}`,
						description: `Switch back to profile "${profile.name}"`,
					},
				],
			},
		});
	}
	if (persistenceFailure) {
		return ctx.error({
			code:
				persistenceFailure.kind === "removed"
					? "PROFILE_REMOVED"
					: "PROFILE_UPDATE_FAILED",
			message:
				persistenceFailure.kind === "removed"
					? `Profile "${profile.name}" was removed while selecting a business — log in again.`
					: `Couldn't update profile "${profile.name}": ${persistenceFailure.detail}. Log in again.`,
			retryable: false,
			cta: {
				commands: [{ command: "auth login", description: "Log in to Whop" }],
			},
		});
	}

	const selected = normalizeAccount(account);
	printSummary(ctx, selected, false);
	return ctx.ok({ account: selected, created: false });
}

function persistAccount(
	dependencies: QuickstartDependencies,
	account: Account,
	profileName: string,
): { kind: "removed" | "failed" | "inactive"; detail?: string } | null {
	try {
		if (!dependencies.setActiveProfileAccount(account, profileName)) {
			return { kind: "removed" };
		}
		return dependencies.getActiveProfile()?.name === profileName
			? null
			: { kind: "inactive" };
	} catch (error) {
		return {
			kind: "failed",
			detail: error instanceof Error ? error.message : "unknown error",
		};
	}
}

function normalizeAccount(account: AccountChoice): Account {
	return { id: account.id, title: account.title, route: account.route };
}

function availableAccountsMessage(
	message: string,
	accounts: AccountChoice[],
): string {
	if (accounts.length === 0) return `${message} Available: none.`;
	return `${message} Available: ${accounts
		.map((account) => `${account.id} (${account.title})`)
		.join(", ")}.`;
}

function createAccountCta() {
	return {
		description: "Create your first business account:",
		commands: [
			{
				command: "quickstart",
				options: { business_name: "Acme" },
				description: "Create and select a business account",
			},
		],
	};
}

function accountSelectionCta(accounts: AccountChoice[], includeCreate = false) {
	return {
		description: "Choose or create a business account:",
		commands: [
			...accounts.map((account) => ({
				command: "quickstart",
				options: { account_id: account.id },
				description: account.title,
			})),
			...(includeCreate ? createAccountCta().commands : []),
		],
	};
}

function printSummary(ctx: RunCtx, account: Account, created: boolean): void {
	if (ctx.agent || ctx.formatExplicit) return;
	const title = account.title ?? account.route ?? account.id;
	const action = created ? "Created and selected" : "Using";
	console.log(
		`\n${colors.success("✓")} ${action} business account ${chalk.bold(`"${title}"`)} ${chalk.dim(`(${account.id})`)}`,
	);
	console.log(chalk.dim("  Run `whop --help` to choose what to do next.\n"));
}
